(() => {
  if (customElements.get('mfs-delivery-estimate')) return;
  const DAY = 86400000;
  const london = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  const dateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
  const parts = date => Object.fromEntries(london.formatToParts(date).filter(p => p.type !== 'literal').map(p => [p.type, Number(p.value)]));
  const key = date => date.toISOString().slice(0, 10);
  const holidays = new Set(["2026-01-01", "2026-04-03", "2026-04-06", "2026-05-04", "2026-05-25", "2026-08-31", "2026-12-25", "2026-12-28", "2027-01-01", "2027-03-26", "2027-03-29", "2027-05-03", "2027-05-31", "2027-08-30", "2027-12-27", "2027-12-28", "2028-01-03", "2028-04-14", "2028-04-17", "2028-05-01", "2028-05-29", "2028-08-28", "2028-12-25", "2028-12-26"]);
  const working = date => ![0, 6].includes(date.getUTCDay()) && !holidays.has(key(date));
  const addDays = (date, amount) => new Date(date.getTime() + amount * DAY);
  const addWorking = (date, amount) => {
    let result = date;
    while (amount > 0) { result = addDays(result, 1); if (working(result)) amount--; }
    return result;
  };
  const deadline = date => {
    const candidate = new Date(date.getTime() + 13 * 3600000);
    const p = parts(candidate);
    const localAsUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return new Date(candidate.getTime() - (localAsUTC - candidate.getTime()));
  };
  const estimate = (now, country) => {
    const p = parts(now);
    const today = new Date(Date.UTC(p.year, p.month - 1, p.day));
    let dispatch = today;
    if (!working(dispatch) || now >= deadline(dispatch)) {
      do { dispatch = addDays(dispatch, 1); } while (!working(dispatch));
    }
    const uk = country === 'GB';
    const start = addWorking(dispatch, uk ? 1 : 2);
    const end = addWorking(dispatch, uk ? 1 : 5);
    return { dispatch, start, end, deadline: deadline(dispatch), sameDay: key(dispatch) === key(today), supported: today.getUTCFullYear() >= 2026 && end.getUTCFullYear() <= 2028 };
  };
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        try {
          this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
          this.selected = this.config.selected;
          this.controller = new AbortController();
          this.querySelector('[data-details-toggle]').addEventListener('click', event => {
            const open = event.currentTarget.getAttribute('aria-expanded') !== 'true';
            event.currentTarget.setAttribute('aria-expanded', String(open));
            this.querySelector('[data-dispatch]').hidden = !open;
            this.querySelector('.mfs-delivery__expanded').hidden = !open;
          }, { signal: this.controller.signal });
          const form = document.getElementById(this.dataset.form);
          form?.addEventListener('variant:change', event => {
            this.selected = event.detail.variant?.id;
            this.update();
          }, { signal: this.controller.signal });
          document.addEventListener('visibilitychange', () => this.update(), { signal: this.controller.signal });
          this.update();
          this.timer = setInterval(() => { if (!document.hidden) this.update(); }, 1000);
        } catch { this.hidden = true; }
      });
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.controller?.abort();
      this.controller = null;
    }
    setText(selector, text) {
      const node = this.querySelector(selector);
      if (node.textContent !== text) node.textContent = text;
    }
    update() {
      const inStock = this.config.variants[String(this.selected)] === true;
      this.hidden = !inStock;
      if (!inStock) return;
      const country = this.dataset.country;
      const uk = country === 'GB';
      const l = this.config.labels;
      const now = new Date();
      const result = estimate(now, country);
      this.setText('[data-service]', uk ? l.uk_service : l.intl_service);
      this.setText('[data-note]', uk ? l.uk_note : l.intl_note);
      this.querySelector('[data-cutoff-row]').hidden = !result.supported;
      if (!result.supported) {
        this.setText('[data-arrival]', uk ? l.typical_uk : l.typical_intl);
        return;
      }
      this.setText('[data-arrival]', uk ? dateFormat.format(result.start) : `${dateFormat.format(result.start)} – ${dateFormat.format(result.end)}`);
      const totalMinutes = Math.floor((result.deadline - now) / 60000);
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor(totalMinutes % 1440 / 60);
      const minutes = totalMinutes % 60;
      const duration = [days ? `${days}${l.days}` : '', hours ? `${hours}${l.hours}` : '', minutes ? `${minutes}${l.minutes}` : ''].filter(Boolean).join(' ') || l.under_minute;
      this.setText('[data-countdown]', duration);
      this.setText('[data-dispatch]', result.sameDay ? l.today : l.next_dispatch.replace('[date]', dateFormat.format(result.dispatch)));
    }
  }
  customElements.define('mfs-delivery-estimate', DeliveryEstimate);
})();
